(function () {
    'use strict';

    document.addEventListener('DOMContentLoaded', () => {
      const {
        hydrateComponents
      } = window.PoleneLibs.Hydration;
      (async () => {
        const components = [Promise.resolve().then(function () { return collectionInfiniteScroll$1; })];
        const componentsObj = hydrateComponents(components);
        await componentsObj.hydrate?.();
      })();
    });

    const {
      BaseComponent
    } = window.PoleneLibs.BaseComponent;
    const {
      getElementByRef
    } = window.PoleneLibs.Refs;
    const {
      intersection
    } = window.PoleneLibs.Observer;
    const {
      dispatchEvent
    } = window.PoleneLibs.Utils;
    const ROOT_MARGIN_BOTTOM_DEFAULT_PX = 1200;
    const ROOT_MARGIN_BOTTOM_SMALL_PX = 600;
    class CollectionInfiniteScroll extends BaseComponent {
      isLoading = false;
      bfcacheRestored = false;
      onMount() {
        this.initializeElements();
        this.setupEventListeners();
        if (this.bfcacheRestored) {
          this.bfcacheRestored = false;
          this.observeSentinel();
          return;
        }
        if (!this.restoreState()) {
          this.observeSentinel();
        }
      }
      onUnmount() {
        this.observer?.disconnect();
        this.observer = undefined;
        this.loadController?.abort();
        this.loadController = undefined;
      }
      initializeElements = () => {
        const {
          infiniteScrollSentinel
        } = getElementByRef('infinite-scroll-sentinel', this);
        this.sentinel = infiniteScrollSentinel;
        const {
          infiniteScrollLive
        } = getElementByRef('infinite-scroll-live', this);
        this.liveRegion = infiniteScrollLive;
        const {
          infiniteScrollSeoLink
        } = getElementByRef('infinite-scroll-seo-link', this);
        this.seoLink = infiniteScrollSeoLink;
        this.section = this.closest('[data-ref="collection-section"]') ?? undefined;
        this.bisModal = document.body.querySelector('component-modal[data-ref="bis-modal"]') ?? undefined;
        this.comingSoonModal = document.body.querySelector('component-modal[data-ref="coming-soon-modal"]') ?? undefined;
        if (this.section) {
          this.grid = this.section.querySelector('[data-ref="grid"]') ?? undefined;
        }
      };
      setupEventListeners = () => {
        const {
          signal
        } = this.controller;
        window.addEventListener('COLLECTION_FILTERS_RENDERED', this.handleFiltersRendered, {
          signal
        });
        window.addEventListener('COLLECTION_LAYOUT_CHANGING', this.handleLayoutChanging, {
          signal
        });
        window.addEventListener('COLLECTION_LAYOUT_CHANGED', this.handleLayoutChanged, {
          signal
        });
        window.addEventListener('pageshow', this.handlePageShow, {
          signal
        });
        document.addEventListener('scrollend', this.handleScrollEnd, {
          signal
        });
      };
      handleFiltersRendered = () => {
        this.loadController?.abort();
        this.loadController = undefined;
        this.isLoading = false;
        this.grid?.setAttribute('aria-busy', 'false');
        this.resetState();
        this.observeSentinel();
      };
      handleLayoutChanging = () => {
        this.observer?.disconnect();
        this.loadController?.abort();
        this.loadController = undefined;
        this.isLoading = false;
        this.grid?.setAttribute('aria-busy', 'false');
      };
      handleLayoutChanged = () => this.observeSentinel();
      handlePageShow = event => {
        if (event.persisted) {
          this.bfcacheRestored = true;
        }
      };
      handleScrollEnd = () => {
        try {
          sessionStorage.setItem(this.getScrollStorageKey(), String(window.scrollY));
        } catch {}
      };
      observeSentinel = () => {
        this.observer?.disconnect();
        if (!this.sentinel) return;
        if (this.dataset.currentPage === this.dataset.totalPages) return;
        const rootMarginBottom = this.isCollectionLayoutSmall() ? ROOT_MARGIN_BOTTOM_SMALL_PX : ROOT_MARGIN_BOTTOM_DEFAULT_PX;
        this.observer = intersection(this.sentinel, this.handleIntersection, {
          rootMargin: `0px 0px ${rootMarginBottom}px 0px`,
          threshold: [0]
        });
      };
      handleIntersection = entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.loadNextPage();
          }
        }
      };
      loadNextPage = async () => {
        const {
          nextUrl,
          sectionId
        } = this.dataset;
        if (this.isLoading || !nextUrl || !this.grid) return;
        this.isLoading = true;
        this.grid.setAttribute('aria-busy', 'true');
        this.loadController?.abort();
        const localController = new AbortController();
        this.loadController = localController;
        try {
          const url = new URL(nextUrl, window.location.origin);
          url.searchParams.set('section_id', sectionId ?? '');
          const response = await fetch(url.toString(), {
            signal: localController.signal
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newProducts = doc.querySelectorAll('component-product-card');
          const fragment = document.createDocumentFragment();
          newProducts.forEach(product => fragment.appendChild(product));
          this.grid.appendChild(fragment);
          const newComponent = doc.querySelector('collection-infinite-scroll');
          if (newComponent) {
            this.dataset.currentPage = newComponent.dataset.currentPage;
            this.dataset.totalPages = newComponent.dataset.totalPages;
            this.dataset.nextUrl = newComponent.dataset.nextUrl;
          }
          this.updateSeoLink();
          this.cacheState();
          this.announceProducts(newProducts.length);
          this.bisModal?.reInit();
          this.comingSoonModal?.reInit();
          dispatchEvent(window, 'PRODUCT_CARD_RENDERED');
          if (this.dataset.currentPage === this.dataset.totalPages) {
            this.observer?.disconnect();
            this.classList.add('hidden');
          } else {
            this.observeSentinel();
          }
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') return;
        } finally {
          if (this.loadController === localController) {
            this.isLoading = false;
            this.grid?.setAttribute('aria-busy', 'false');
            this.loadController = undefined;
          }
        }
      };
      isCollectionLayoutSmall = () => {
        return this.closest('[data-layout]')?.dataset.layout === 'small';
      };
      announceProducts = count => {
        if (!this.liveRegion || !this.grid) return;
        const shown = this.grid.querySelectorAll('component-product-card').length;
        const total = parseInt(this.dataset.totalProducts ?? '0', 10);
        this.liveRegion.textContent = `${count} products added. Showing ${shown} of ${total} products.`;
      };
      updateSeoLink = () => {
        const {
          nextUrl
        } = this.dataset;
        if (!this.seoLink) return;
        if (this.dataset.currentPage === this.dataset.totalPages) {
          this.seoLink.remove();
          this.seoLink = undefined;
        } else if (nextUrl) {
          this.seoLink.href = nextUrl;
        }
      };
      getStorageKey = () => {
        const collectionId = this.dataset.collectionId ?? '';
        const searchParams = new URL(window.location.href).searchParams.toString();
        return `infinite-scroll:${collectionId}:${searchParams}`;
      };
      getScrollStorageKey = () => {
        const collectionId = this.dataset.collectionId ?? '';
        const searchParams = new URL(window.location.href).searchParams.toString();
        return `infinite-scroll-scrolly:${collectionId}:${searchParams}`;
      };
      restoreState = () => {
        try {
          const navEntry = performance.getEntriesByType('navigation')[0];
          if (navEntry?.type !== 'back_forward') return false;
          const key = this.getStorageKey();
          const raw = sessionStorage.getItem(key);
          if (!raw || !this.grid) return false;
          const cached = JSON.parse(raw);
          this.grid.innerHTML = cached.gridHTML;
          this.dataset.currentPage = cached.currentPage;
          this.dataset.totalPages = cached.totalPages;
          this.dataset.nextUrl = cached.nextUrl;
          this.updateSeoLink();
          this.bisModal?.reInit();
          this.comingSoonModal?.reInit();
          dispatchEvent(window, 'PRODUCT_CARD_RENDERED');
          const scrollYRaw = sessionStorage.getItem(this.getScrollStorageKey());
          if (scrollYRaw) {
            void document.documentElement.scrollHeight;
            window.scrollTo({
              top: parseInt(scrollYRaw, 10),
              left: 0,
              behavior: 'instant'
            });
          }
          this.observeSentinel();
          return true;
        } catch {
          return false;
        }
      };
      cacheState = () => {
        if (!this.grid) return;
        try {
          const state = {
            gridHTML: this.grid.innerHTML,
            currentPage: this.dataset.currentPage ?? '1',
            totalPages: this.dataset.totalPages ?? '1',
            nextUrl: this.dataset.nextUrl ?? ''
          };
          sessionStorage.setItem(this.getStorageKey(), JSON.stringify(state));
        } catch {}
      };
      resetState = () => {
        try {
          const collectionId = this.dataset.collectionId ?? '';
          for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key?.startsWith(`infinite-scroll:${collectionId}:`) || key?.startsWith(`infinite-scroll-scrolly:${collectionId}:`)) {
              sessionStorage.removeItem(key);
            }
          }
        } catch {}
        this.observer?.disconnect();
      };
      static define = () => {
        if (!customElements.get('collection-infinite-scroll')) {
          customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
        }
      };
    }
    var collectionInfiniteScroll = () => {
      CollectionInfiniteScroll.define();
    };

    var collectionInfiniteScroll$1 = /*#__PURE__*/Object.freeze({
        __proto__: null,
        CollectionInfiniteScroll: CollectionInfiniteScroll,
        default: collectionInfiniteScroll
    });

})();
